import angular from 'angular'

import deline from '../utils/deline'
import MapSimple from '../utils/map-simple'
import StrictObj from '../utils/strict-obj'
import debugFields from '../utils/debug-fields'
import extractRawElm from '../utils/extract-raw-elm'

import {
  isEmpty, safeGet, defaultTo, isNotEmpty,
  safeAssign, isElementLike, isEmptyObject
} from '../utils/bits'

import {
  get, map, trim, assign, flatten, without,
  isString, fromPairs, camelCase, isObjectLike
} from 'lodash-es'

import { getElmStr } from '../utils/get-elm-str'

angular
  .module('ggs.animate')
  .factory('animate', AnimateProvider)

function AnimateProvider(
  $q,
  qh,
  domh,
  rootQs,
  debugObj,
  $rootScope,
  addIdPrefix,
  StayInQueue,
  isDisplayedAsync,
  WaitTransitionEnd) {

  const FORCE = true
  const ANIMATE = false

  const OPACITY = 'opacity'
  const TRANSITION = 'transition'

  const TRANSITION_NONE = 'none'
  const TRANSITION_ZERO_ALL = 'all 0s'
  const TRANSITION_ZERO_OPACITY = `${OPACITY} 0s`

  const SHOW_BEFORE = 'ggs-animate__show_before'
  const SHOW_ACTIVE = 'ggs-animate__show_active'
  const SHOW_AFTER = 'ggs-animate__show_after'
  const SHOW_AFTER_SOFT = 'ggs-animate__show_after-soft'

  const HIDE_BEFORE = 'ggs-animate__hide_before'
  const HIDE_ACTIVE = 'ggs-animate__hide_active'
  const HIDE_AFTER = 'ggs-animate__hide_after'
  const HIDE_AFTER_SOFT = 'ggs-animate__hide_after-soft'

  const ALL_SHOW_AFTER = [SHOW_AFTER, SHOW_AFTER_SOFT]
  const ALL_HIDE_AFTER = [HIDE_AFTER, HIDE_AFTER_SOFT]

  const DEFAULT_OPACITY_TRANSITION = `${OPACITY} 0.3s`

  const elmQueues = MapSimple()
  const trnsStyles = MapSimple()

  const animate = StrictObj({
    protoShow: createMethod(ANIMATE, 'protoShow', protoIsVisible, SHOW_BEFORE, SHOW_ACTIVE, SHOW_AFTER, ALL_SHOW_AFTER, ALL_HIDE_AFTER),
    protoHide: createMethod(ANIMATE, 'protoHide', protoIsHidden, HIDE_BEFORE, HIDE_ACTIVE, HIDE_AFTER, ALL_HIDE_AFTER, ALL_SHOW_AFTER),

    protoShowSoft: createMethod(ANIMATE, 'protoShowSoft', protoIsVisible, SHOW_BEFORE, SHOW_ACTIVE, SHOW_AFTER_SOFT, ALL_SHOW_AFTER, ALL_HIDE_AFTER),
    protoHideSoft: createMethod(ANIMATE, 'protoHideSoft', protoIsHidden, HIDE_BEFORE, HIDE_ACTIVE, HIDE_AFTER_SOFT, ALL_HIDE_AFTER, ALL_SHOW_AFTER),

    protoShowForce: createMethod(FORCE, 'protoShowForce', protoIsVisible, SHOW_BEFORE, SHOW_ACTIVE, SHOW_AFTER, ALL_SHOW_AFTER, ALL_HIDE_AFTER),
    protoHideForce: createMethod(FORCE, 'protoHideForce', protoIsHidden, HIDE_BEFORE, HIDE_ACTIVE, HIDE_AFTER, ALL_HIDE_AFTER, ALL_SHOW_AFTER),

    protoIsHidden,
    protoIsVisible
  })

  safeAssign(animate, {
    show: makeArg(makeStayInElmQueue(animate.protoShow)),
    hide: makeArg(makeStayInElmQueue(animate.protoHide)),

    showSoft: makeArg(makeStayInElmQueue(animate.protoShowSoft)),
    hideSoft: makeArg(makeStayInElmQueue(animate.protoHideSoft)),

    showForce: makeArg(makeStayInElmQueue(animate.protoShowForce)),
    hideForce: makeArg(makeStayInElmQueue(animate.protoHideForce)),

    isHidden: makeArg(makeStayInElmQueue(animate.protoIsHidden)),
    isVisible: makeArg(makeStayInElmQueue(animate.protoIsVisible)),

    ifHidden: makeArg(makeIf(makeStayInElmQueue(animate.protoIsHidden))),
    ifVisible: makeArg(makeIf(makeStayInElmQueue(animate.protoIsVisible)))
  })

  safeAssign(animate, {
    makeShow: makeBind(animate.show),
    makeHide: makeBind(animate.hide),

    makeShowSoft: makeBind(animate.showSoft),
    makeHideSoft: makeBind(animate.hideSoft),

    makeShowForce: makeBind(animate.showForce),
    makeHideForce: makeBind(animate.hideForce),

    makeIsHidden: makeBind(animate.isHidden),
    makeIsVisible: makeBind(animate.isVisible),

    makeIfHidden: makeBind(animate.ifHidden),
    makeIfVisible: makeBind(animate.ifVisible)
  })

  debugFields(animate, {
    _elmQueues: elmQueues,
    _trnsStyles: trnsStyles,
    _regTrns: regTrns,
    _trns2obj: trns2obj,
    _obj2trns: obj2trns,
    _mergeTrns: mergeTrns,
    _extractOpacityTrnsMs: extractOpacityTrnsMs,
    _extractTrnsMs: extractTrnsMs
  })

  $rootScope.$on('$destroy', onDestroy)

  return debugObj('animate', animate)

  function onDestroy() {
    trnsStyles.values().forEach((style) => style.detach())
    trnsStyles.clear()
  }

  function makeBind(action) {
    return function bind(...initArgs) {
      return function wrapper(...callArgs) {
        return action(...initArgs, ...callArgs)
      }
    }
  }

  function makeArg(action) {
    return function castArg(orig, ...rest) {
      const isElm = isElementLike(orig)
      const containsElm = isElementLike(safeGet(orig, 'elm'))

      const $elm = isElm
        ? orig
        : containsElm
          ? orig.elm
          : orig.$elm

      if (!isElementLike($elm)) {
        throw new Error('animate#makeArg: $elm should be an element!')
      }

      const transition = safeGet(orig, 'transition', null)
      const arg = StrictObj({ $elm, transition, orig })
      return action(arg, ...rest)
    }
  }

  function makeStayInElmQueue(method) {
    return function stayInElmQueue(arg) {
      const elm = extractRawElm(arg.$elm)
      const stayInQueue = elmQueues.has(elm)
        ? elmQueues.get(elm)
        : elmQueues.set(elm, StayInQueue(`animate[elm=${getElmStr(elm)}]`))
      return stayInQueue(() => method(arg)).finally(() => {
        if (stayInQueue.idle()) elmQueues.del(elm)
      })
    }
  }

  function makeIf(action) {
    return function ifAction(arg, then) {
      return action(arg).then((it) => it ? then(arg.orig) : arg.orig)
    }
  }

  function protoIsVisible({ $elm }) {
    const elm = extractRawElm($elm)
    const hasHideClass = ALL_HIDE_AFTER.some((cl) => elm.classList.contains(cl))
    const hasShowClass = ALL_SHOW_AFTER.some((cl) => elm.classList.contains(cl))
    if (hasHideClass) return false
    if (hasShowClass) return true
    return true
  }

  function protoIsHidden({ $elm }) {
    const elm = extractRawElm($elm)
    const hasHideClass = ALL_HIDE_AFTER.some((cl) => elm.classList.contains(cl))
    const hasShowClass = ALL_SHOW_AFTER.some((cl) => elm.classList.contains(cl))
    if (hasHideClass) return true
    if (hasShowClass) return false
    return false
  }

  function createMethod(
    force,
    _name,
    check,
    before,
    active,
    after,
    applied,
    inverse) {

    return method

    function forceMethod({ $elm, orig }) {
      const elm = extractRawElm($elm)
      delClasses(elm, inverse)
      delClasses(elm, applied)
      addClasses(elm, after)
      return orig
    }

    function method({ $elm, transition, orig }) {
      const processed = check({ $elm })
      if (force) return forceMethod({ $elm, orig })
      if (processed) return forceMethod({ $elm, orig })

      const elm = extractRawElm($elm)
      return isDisplayedAsync(elm.parentNode).then((displayed) => {
        if (!displayed) return forceMethod({ $elm, orig })

        // reset
        delClasses(elm, inverse)
        delClasses(elm, applied)
        delClasses(elm, before, active, after)

        // calc
        const usrTrns = obj2trns(defaultTo(transition, null))
        const beforeTrns = TRANSITION_ZERO_OPACITY
        const activeTrns = mergeTrns(without([DEFAULT_OPACITY_TRANSITION, usrTrns], TRANSITION_ZERO_ALL, TRANSITION_NONE))
        const trnsWaitMs = 1.5 * extractOpacityTrnsMs(activeTrns)

        // reg
        const beforeAttr = regTrns(beforeTrns)
        const activeAttr = regTrns(activeTrns)

        return qh.try(() => {
          // before
          elm.setAttribute(beforeAttr, true)
          addClasses(elm, before)
          return $q(requestAnimationFrame)
        }).then(() => {
          // active
          elm.removeAttribute(beforeAttr)
          elm.setAttribute(activeAttr, true)
          addClasses(elm, active)
          return WaitTransitionEnd(trnsWaitMs, elm, OPACITY)
        }).then(() => {
          // style
          elm.removeAttribute(activeAttr)
          // after
          delClasses(elm, before)
          delClasses(elm, active)
          addClasses(elm, after)
          return orig
        })
      })
    }
  }

  function regTrns(trns) {
    const marker = addIdPrefix('animate_' + camelCase(trns))
    if (!trnsStyles.has(marker)) {
      const css = `${rootQs} [${marker}] {\n\t${TRANSITION}: ${trns};\n}`
      const style = domh.createStyle(marker).local.important(css).attach()
      trnsStyles.set(marker, style)
    }
    return marker
  }

  function trns2obj(rawTrns) {
    if (isObjectLike(rawTrns)) {
      return rawTrns
    }

    if (isEmpty(rawTrns)) {
      return {}
    }

    const trns = deline(rawTrns)
    const obj = fromPairs(trns.split(',').map(trim).map((bit) => {
      const re = /^([a-z-]+)\s(.+)$/i
      const key = get(bit.match(re), 1)
      const val = get(bit.match(re), 2)
      return [key, val]
    }))

    return obj
  }

  function obj2trns(obj) {
    if (isString(obj)) {
      return obj
    }

    if (isEmpty(obj)) {
      return ''
    }

    if (isEmptyObject(obj)) {
      return ''
    }

    const trns = map(obj, (val, key) => `${key} ${val}`).sort().join(', ')
    return trns
  }

  function mergeTrns(...trns) {
    const obj = assign(...flatten(trns).filter(isNotEmpty).map(trns2obj))
    const trn = obj2trns(obj)
    return trn
  }

  function extractOpacityTrnsMs(rawTrns) {
    return extractTrnsMs(OPACITY, rawTrns)
  }

  function extractTrnsMs(property, rawTrns) {
    const obj = trns2obj(rawTrns)
    const val = trim(obj[property])
    const time = val.match(/^([\d.]+m?s)/i)[1]
    const ms = /ms$/i.test(time)
      ? parseFloat(time)
      : parseFloat(time) * 1000

    return ms
  }

  function addClasses($elm, ...classes) {
    const elm = extractRawElm($elm)
    flatten(classes).forEach((className) => elm.classList.add(className))
    return $elm
  }

  function delClasses($elm, ...classes) {
    const elm = extractRawElm($elm)
    flatten(classes).forEach((className) => elm.classList.remove(className))
    return $elm
  }
}
